/**
 * Activity-group helpers (ticket 3525, upstream pin e3e7cc3f, MIT), split out
 * of the ported `MessagesTimeline.logic.ts`: which entries join the shared
 * activity row, tool-group identity, and the trailing-tool attachment that
 * makes settled text and tools read as one response.
 *
 * @module MessagesTimeline.activityGroups
 */

import type { TurnId } from "@cmmd-forge/contracts";
import {
  workEntryDisplayIndicatesToolFailure,
  workEntryIndicatesToolNeutralStatus,
  workLogEntryIsToolLike,
  type TimelineEntry,
  type WorkLogEntry,
} from "../../session-logic";
import { resolveWorkEntryToolPresentation } from "../../workLogToolPresentation";
import { summarizeToolGroup, toolGroupAction, toolGroupSummaryKind } from "../../workLogToolGroups";
import { singleToolCallLabel } from "./MessagesTimeline.labels";
import type { ActivityEntry, MessagesTimelineRow } from "./MessagesTimeline.rowTypes";

export function workEntryIsVisibleInGroup(
  entry: WorkLogEntry,
  expandedToolGroupEntry = false,
): boolean {
  return (
    (expandedToolGroupEntry &&
      (entry.toolLifecycleStatus === "inProgress" ||
        entry.sourceActivityKind === "task.progress")) ||
    !workEntryIndicatesToolNeutralStatus(entry)
  );
}

export function isActivityEntry(entry: TimelineEntry): entry is ActivityEntry {
  return entry.kind === "message"
    ? entry.message.role === "reasoning"
    : entry.kind === "work" &&
        entry.entry.agentSpawn === undefined &&
        entry.entry.questionAnswer === undefined &&
        entry.entry.githubConfirmation === undefined &&
        entry.entry.githubActionRequired === undefined &&
        entry.entry.sourceActivityKind !== "context-compaction" &&
        entry.entry.tone !== "error";
}

export function deriveTerminalAssistantMessageIds(timelineEntries: ReadonlyArray<TimelineEntry>) {
  const lastAssistantMessageIdByResponseKey = new Map<string, string>();
  let nullTurnResponseIndex = 0;

  for (const timelineEntry of timelineEntries) {
    if (timelineEntry.kind !== "message") {
      continue;
    }
    const { message } = timelineEntry;
    if (message.role === "user") {
      nullTurnResponseIndex += 1;
      continue;
    }
    if (message.role !== "assistant") {
      continue;
    }

    const responseKey = message.turnId
      ? `turn:${message.turnId}`
      : `unkeyed:${nullTurnResponseIndex}`;
    lastAssistantMessageIdByResponseKey.set(responseKey, message.id);
  }

  return new Set(lastAssistantMessageIdByResponseKey.values());
}

export function workEntryIsActiveTurnActivity(entry: WorkLogEntry): boolean {
  return (
    entry.toolLifecycleStatus === "inProgress" ||
    (entry.toolLifecycleStatus === undefined &&
      (entry.sourceActivityKind === "task.progress" || workLogEntryIsToolLike(entry)))
  );
}

export function workGroupIdentity(timelineEntryId: string, entry: WorkLogEntry): string {
  return entry.toolCallId
    ? `tool:${entry.turnId ?? "no-turn"}:${entry.toolCallId}`
    : timelineEntryId;
}

export function workGroupId(timelineEntryId: string, entry: WorkLogEntry): string {
  return `work-group:${workGroupIdentity(timelineEntryId, entry)}`;
}

export function expandedWorkGroupRow(
  groupId: string,
  createdAt: string,
  groupedEntries: WorkLogEntry[],
): Extract<MessagesTimelineRow, { kind: "work" }> {
  return {
    kind: "work",
    id: `${groupId}:details`,
    createdAt,
    groupedEntries,
    isExpandedToolGroup: true,
  };
}

/**
 * Builds the collapsed tool-group toggle row: one summary line (with the
 * group's integration source, surface, or action icon) standing in for the
 * grouped calls it expands to.
 */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function buildWorkGroupToggleRow(input: {
  readonly visibleGroupedEntries: WorkLogEntry[];
  readonly groupId: string;
  readonly timelineEntryId: string;
  readonly createdAt: string;
  readonly turnId: TurnId | null;
  readonly expanded: boolean;
}): Extract<MessagesTimelineRow, { kind: "work-toggle" }> {
  const { visibleGroupedEntries, groupId, timelineEntryId, createdAt, turnId, expanded } = input;
  const summaryKind = toolGroupSummaryKind(visibleGroupedEntries);
  const primarySourceEntry = visibleGroupedEntries.find((entry) => entry.toolSource !== undefined);
  const primarySourceKey = primarySourceEntry?.toolSource?.key;
  const primarySourceIcon = primarySourceKey
    ? (visibleGroupedEntries.find(
        (entry) => entry.toolSource?.key === primarySourceKey && entry.toolIcon !== undefined,
      )?.toolIcon ?? primarySourceEntry?.toolSource?.icon)
    : undefined;
  const groupToolSurface =
    primarySourceEntry?.toolSurface ??
    visibleGroupedEntries.findLast((entry) => entry.toolSurface !== undefined)?.toolSurface;
  const groupToolIcon =
    primarySourceIcon ??
    visibleGroupedEntries.findLast((entry) => entry.toolIcon !== undefined)?.toolIcon;
  const latestToolEntry = visibleGroupedEntries.findLast(workLogEntryIsToolLike);
  const singleEntry =
    visibleGroupedEntries.length === 1 ? (visibleGroupedEntries[0] ?? null) : null;
  const usesSingleToolCallLabel =
    singleEntry !== null &&
    workLogEntryIsToolLike(singleEntry) &&
    toolGroupAction(singleEntry) !== "edit";
  const summaryToolIcon = usesSingleToolCallLabel
    ? resolveWorkEntryToolPresentation(singleEntry!, "completed")?.icon
    : undefined;
  return {
    kind: "work-toggle",
    id: `work-toggle:${timelineEntryId}`,
    createdAt,
    turnId,
    groupId,
    hiddenCount: visibleGroupedEntries.length,
    expanded,
    summary: usesSingleToolCallLabel
      ? singleToolCallLabel(singleEntry!)
      : singleEntry !== null && !workLogEntryIsToolLike(singleEntry)
        ? singleEntry.label
        : summarizeToolGroup(visibleGroupedEntries),
    summaryKind,
    ...(groupToolSurface ? { toolSurface: groupToolSurface } : {}),
    ...(groupToolIcon ? { toolIcon: groupToolIcon } : {}),
    ...(summaryToolIcon ? { summaryToolIcon } : {}),
    hasFailure:
      latestToolEntry !== undefined && workEntryDisplayIndicatesToolFailure(latestToolEntry),
  };
}

/**
 * When a settled turn ends with tool calls after its terminal text, treat the
 * text and tools as one visual response. The message metadata becomes the
 * footer for the whole block instead of separating the prose from the tools.
 */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function attachTrailingToolGroupsToAssistant(
  rows: ReadonlyArray<MessagesTimelineRow>,
): MessagesTimelineRow[] {
  const messageRowsWithoutMeta = new Set<string>();
  const metaRowsAfterIndex = new Map<
    number,
    Extract<MessagesTimelineRow, { kind: "assistant-meta" }>
  >();

  for (const [messageIndex, row] of rows.entries()) {
    const turnId = row.kind === "message" ? (row.message.turnId ?? null) : null;
    if (
      row.kind !== "message" ||
      row.message.role !== "assistant" ||
      !row.showAssistantMeta ||
      turnId === null
    ) {
      continue;
    }

    let lastTrailingWorkIndex = -1;
    let hasTrailingToolGroup = false;
    for (let index = messageIndex + 1; index < rows.length; index += 1) {
      const candidate = rows[index];
      if (!candidate) {
        break;
      }
      // A thinking block can follow the answer (the next one starts before its
      // tool call); it is not another message in the conversation.
      if (candidate.kind === "message" && candidate.message.role === "reasoning") {
        continue;
      }
      if (candidate.kind === "message") {
        break;
      }
      if (
        (candidate.kind === "work-toggle" ||
          (candidate.kind === "activity-group" &&
            candidate.entries.some((entry) => entry.kind === "work"))) &&
        candidate.turnId === turnId
      ) {
        hasTrailingToolGroup = true;
        lastTrailingWorkIndex = index;
        continue;
      }
      if (
        candidate.kind === "work" &&
        candidate.groupedEntries.some((entry) => entry.turnId === turnId)
      ) {
        if (
          !candidate.isExpandedToolGroup &&
          candidate.groupedEntries.some(workLogEntryIsToolLike)
        ) {
          hasTrailingToolGroup = true;
        }
        if (hasTrailingToolGroup) {
          lastTrailingWorkIndex = index;
        }
      }
    }

    if (lastTrailingWorkIndex < 0) {
      continue;
    }

    messageRowsWithoutMeta.add(row.id);
    metaRowsAfterIndex.set(lastTrailingWorkIndex, {
      kind: "assistant-meta",
      id: `assistant-meta:${row.message.id}`,
      createdAt: rows[lastTrailingWorkIndex]?.createdAt ?? row.message.createdAt,
      message: row.message,
      showAssistantCopyButton: row.showAssistantCopyButton,
      assistantCopyStreaming: row.assistantCopyStreaming,
    });
  }

  const result: MessagesTimelineRow[] = [];
  for (const [index, row] of rows.entries()) {
    if (row.kind === "message" && messageRowsWithoutMeta.has(row.id)) {
      result.push({ ...row, showAssistantMeta: false, showAssistantCopyButton: false });
    } else {
      result.push(row);
    }
    const metaRow = metaRowsAfterIndex.get(index);
    if (metaRow) {
      result.push(metaRow);
    }
  }
  return result;
}

export type { TurnId };
